// Stats storage in IndexedDB
import { getItem, setItem } from '../storage/db';

export interface GameRecord {
  id: string;
  difficulty: 'easy' | 'medium' | 'hard';
  result: 'win' | 'loss' | 'draw';
  moves: number;
  durationSeconds: number;
  playedAt: string; // ISO date
}

export interface PuzzleRecord {
  puzzleId: string;
  category: string;
  solved: boolean;
  attempts: number;
  solvedAt?: string;
}

export interface Stats {
  games: GameRecord[];
  puzzles: PuzzleRecord[];
}

const STATS_KEY = 'chesskid_stats';

export async function getStats(): Promise<Stats> {
  return (await getItem<Stats>(STATS_KEY)) ?? { games: [], puzzles: [] };
}

export async function saveStats(stats: Stats): Promise<void> {
  await setItem(STATS_KEY, stats);
}

export async function addGameRecord(record: GameRecord): Promise<void> {
  const stats = await getStats();
  stats.games.push(record);
  await saveStats(stats);
}

export async function addPuzzleRecord(record: PuzzleRecord): Promise<void> {
  const stats = await getStats();
  const existing = stats.puzzles.findIndex(p => p.puzzleId === record.puzzleId);
  if (existing >= 0) {
    stats.puzzles[existing] = record;
  } else {
    stats.puzzles.push(record);
  }
  await saveStats(stats);
}

// Calculated stats
export async function getGameStats() {
  const stats = await getStats();
  const total = stats.games.length;
  const wins = stats.games.filter(g => g.result === 'win').length;
  const losses = stats.games.filter(g => g.result === 'loss').length;
  const draws = stats.games.filter(g => g.result === 'draw').length;
  const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;

  return { total, wins, losses, draws, winRate };
}

export async function getPuzzleStats() {
  const stats = await getStats();
  const total = stats.puzzles.length;
  const solved = stats.puzzles.filter(p => p.solved).length;

  return { total, solved };
}
